/* eslint-disable react/no-array-index-key */

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FieldArray, useFormikContext } from 'formik';
import { Row, Col } from 'reactstrap';
import { startCase } from 'lodash';
import { Button, Field, CardTitle } from '@cordata/buffet';

import LagCalculationForm from './LagCalculationForm';
import CountColumnForm from './CountColumnForm';

const lagTableColumnTypeOptions = [
  { label: 'Group By Column', value: 'group' },
  { label: 'Row Count', value: 'rowCount' },
  { label: 'Lag Calculation', value: 'lagCalculation' },
  { label: 'Count Column', value: 'countColumn' },
  { label: 'Division Names', value: 'divisionNames' },
];

const LagTableForm = ({ index, availableReports }) => {
  const {
    values,
    setFieldValue,
    setFieldError,
    setFieldTouched,
  } = useFormikContext();

  const [columnOptions, setColumnOptions] = useState([]);
  const [dateColumnOptions, setDateColumnOptions] = useState([]);
  const [previousColumns, setPreviousColumns] = useState([]);

  useEffect(() => {
    const reportView = values.lagTables[index].reportView || values.reportView;

    if (reportView) {
      setColumnOptions(reportView.columns);
      setDateColumnOptions(reportView.columns.filter(c =>
        ['date', 'time', 'dateTime'].includes(c.type)));

      if (values.lagTables[index].groupByColumns) {
        values.lagTables[index].groupByColumns.forEach((col, colIndex) => {
          if (col && !reportView.columns.some(c => c.value === col.value)) {
            const field = `lagTables.${index}.groupByColumns.${colIndex}`;
            const errorMessage = `Column "${col.value}" is not present in ${reportView.value}.`;

            setFieldError(field, errorMessage);
            setFieldTouched(field, true);
          }
        });
      }
    }
  }, [values.reportView, values.lagTables, index]);

  useEffect(() => {
    const currentColumns = values.lagTables[index].columns || [];

    currentColumns.forEach((col, colIndex) => {
      const prevCol = previousColumns[colIndex];

      if (col.type && col.sourceKey && !col.displayHeader) {
        if (
          !prevCol ||
          prevCol.type?.value !== col.type?.value ||
          prevCol.sourceKey?.value !== col.sourceKey?.value
        ) {
          const header = getDefaultDisplayHeader(col.type, col.sourceKey);
          if (header) {
            setFieldValue(
              `lagTables.${index}.columns.${colIndex}.displayHeader`,
              header,
            );
          }
        }
      }
    });

    setPreviousColumns(currentColumns.map(c => ({ ...c })));
  }, [values.lagTables[index].columns]);

  const getSourceKeyOptions = columnType => {
    if (!columnType) return [];

    switch (columnType.value) {
      case 'group':
        return (values.lagTables[index].groupByColumns || []).map(col => ({
          label: col.label || col.value,
          value: col.value,
        }));
      case 'lagCalculation':
        return (values.lagTables[index].lagCalculations || []).map(lc => ({
          label: lc.label,
          value: lc.label,
        }));
      case 'countColumn':
        return (values.lagTables[index].additionalCountColumns || []).map(cc => ({
          label: cc.label,
          value: cc.label,
        }));
      case 'rowCount':
        return [{ label: 'Row Count', value: 'rowCount' }];
      case 'divisionNames':
        return [{ label: 'Division Names', value: 'divisionNames' }];
      default:
        return [];
    }
  };

  const getDefaultDisplayHeader = (columnType, sourceKey) => {
    if (!columnType || !sourceKey) return '';

    switch (columnType.value) {
      case 'group':
        return startCase(sourceKey.value || sourceKey);
      case 'lagCalculation': {
        const lagCalc = (values.lagTables[index].lagCalculations || []).find(lc => lc.label === (sourceKey.value || sourceKey));
        if (
          lagCalc &&
          lagCalc.businessDaysMode &&
          lagCalc.businessDaysMode.value !== 'none'
        ) {
          return `${sourceKey.value || sourceKey} (BD)`;
        }
        return sourceKey.value || sourceKey;
      }
      case 'countColumn':
        return sourceKey.value || sourceKey;
      case 'rowCount':
        return 'Row Count';
      case 'divisionNames':
        return 'Divisions';
      default:
        return '';
    }
  };

  return (
    <>
      <Row form>
        <Col sm>
          <Field name={`lagTables.${index}.title`} label="Table Title" />
        </Col>

        <Col sm>
          <Field
            name={`lagTables.${index}.reportView`}
            label="Report View"
            text="If left blank, will use base report view"
            type="select"
            options={availableReports}
          />
        </Col>
      </Row>

      <Row form>
        <Col sm>
          <Field
            name={`lagTables.${index}.groupByColumns`}
            label="Group By Columns"
            text="Columns to group data by. Order determines nesting (first = outer, last = inner)"
            type="select"
            multi
            options={columnOptions}
            required
          />
        </Col>

        <Col sm={3}>
          <Field
            name={`lagTables.${index}.pageSize`}
            label="Page Size"
            text="Number of rows per page"
            type="number"
            required
          />
        </Col>
      </Row>

      <Row form>
        <Col sm>
          <Field
            name={`lagTables.${index}.divisionNameColumn`}
            label="Division Name Column"
            text="Column containing division names (for Division Names column type)"
            type="select"
            options={columnOptions}
          />
        </Col>
      </Row>

      <fieldset className="mt-3">
        <CardTitle tag="h6">Lag Calculations</CardTitle>
        <p className="text-muted small">
          Define time difference calculations between date columns.
        </p>

        <FieldArray
          name={`lagTables.${index}.lagCalculations`}
          render={({ push, remove }) => (
            <>
              {values.lagTables[index].lagCalculations &&
                values.lagTables[index].lagCalculations.map((calc, calcIndex) => (
                  <fieldset key={calcIndex} className="border p-3 mb-3">
                    <Button
                      size="sm"
                      outline
                      spear
                      color="danger"
                      type="button"
                      onClick={() => remove(calcIndex)}
                    >
                      <FontAwesomeIcon icon="minus" className="mr-2" />
                      Remove Lag Calculation
                    </Button>

                    <LagCalculationForm
                      lagTableIndex={index}
                      lagCalcIndex={calcIndex}
                      columnOptions={dateColumnOptions}
                    />
                  </fieldset>
                  ))}

              <Button
                size="sm"
                outline
                color="tertiary"
                spear
                type="button"
                onClick={() =>
                  push({
                    businessDaysMode: { label: 'None', value: 'none' },
                  })
                }
              >
                <FontAwesomeIcon icon="plus" className="mr-2" />
                Add Lag Calculation
              </Button>
            </>
          )}
        />
      </fieldset>

      <fieldset className="mt-3">
        <CardTitle tag="h6">Additional Count Columns</CardTitle>
        <p className="text-muted small">
          Add columns that count rows based on conditions.
        </p>

        <FieldArray
          name={`lagTables.${index}.additionalCountColumns`}
          render={({ push, remove }) => (
            <>
              {values.lagTables[index].additionalCountColumns &&
                values.lagTables[index].additionalCountColumns.map((countCol, countIndex) => (
                  <fieldset key={countIndex} className="border p-3 mb-3">
                    <Button
                      size="sm"
                      outline
                      spear
                      color="danger"
                      type="button"
                      onClick={() => remove(countIndex)}
                    >
                      <FontAwesomeIcon icon="minus" className="mr-2" />
                      Remove Count Column
                    </Button>

                    <CountColumnForm
                      lagTableIndex={index}
                      countIndex={countIndex}
                      columnOptions={columnOptions}
                    />
                  </fieldset>
                  ))}

              <Button
                size="sm"
                outline
                color="tertiary"
                spear
                type="button"
                onClick={() =>
                  push({
                    countCondition: { label: 'Is not null', value: 'isNotNull' },
                  })
                }
              >
                <FontAwesomeIcon icon="plus" className="mr-2" />
                Add Count Column
              </Button>
            </>
          )}
        />
      </fieldset>

      <fieldset className="mt-3">
        <CardTitle tag="h6">Table Columns</CardTitle>
        <p className="text-muted small">
          Configure which columns to display and their order. Use the arrows to
          reorder.
        </p>

        <FieldArray
          name={`lagTables.${index}.columns`}
          render={({ push, remove, move }) => (
            <>
              {values.lagTables[index].columns &&
                values.lagTables[index].columns.map((col, colIndex) => (
                  <fieldset key={colIndex} className="border p-3 mb-3">
                    <Button
                      size="sm"
                      outline
                      spear
                      color="danger"
                      type="button"
                      onClick={() => remove(colIndex)}
                    >
                      <FontAwesomeIcon icon="minus" className="mr-2" />
                      Remove Column
                    </Button>

                    <Row form>
                      {values.lagTables[index].columns.length > 1 && (
                        <Col md={1}>
                          <Row form style={{ height: '50%' }}>
                            <Button
                              className="m-auto"
                              outline
                              type="button"
                              onClick={() => move(colIndex, colIndex - 1)}
                              disabled={colIndex === 0}
                            >
                              <FontAwesomeIcon icon="caret-up" />
                            </Button>
                          </Row>

                          <Row form style={{ height: '50%' }}>
                            <Button
                              className="m-auto"
                              outline
                              type="button"
                              onClick={() => move(colIndex, colIndex + 1)}
                              disabled={
                                colIndex ===
                                values.lagTables[index].columns.length - 1
                              }
                            >
                              <FontAwesomeIcon icon="caret-down" />
                            </Button>
                          </Row>
                        </Col>
                      )}

                      <Col sm>
                        <Field
                          name={`lagTables.${index}.columns.${colIndex}.type`}
                          label="Column Type"
                          type="select"
                          options={lagTableColumnTypeOptions}
                          required
                        />
                      </Col>

                      <Col sm>
                        <Field
                          name={`lagTables.${index}.columns.${colIndex}.sourceKey`}
                          label="Source"
                          text="Which data to display"
                          type="select"
                          options={getSourceKeyOptions(col.type)}
                          required
                        />
                      </Col>

                      <Col sm>
                        <Field
                          name={`lagTables.${index}.columns.${colIndex}.displayHeader`}
                          label="Display Header"
                          text="The column header shown in the UI"
                          required
                        />
                      </Col>
                    </Row>

                    <Row form>
                      <Col sm="auto">
                        <Field
                          name={`lagTables.${index}.columns.${colIndex}.showCustomNullText`}
                          label="Display custom text for null values?"
                          type="checkbox"
                        />
                      </Col>

                      {col.showCustomNullText && (
                        <Col sm>
                          <Field
                            name={`lagTables.${index}.columns.${colIndex}.nullText`}
                            label="Null value text"
                            text="This text will be displayed when the column value is null"
                          />
                        </Col>
                      )}
                    </Row>
                  </fieldset>
                ))}

              <Button
                size="sm"
                outline
                color="tertiary"
                spear
                type="button"
                onClick={() => push({})}
              >
                <FontAwesomeIcon icon="plus" className="mr-2" />
                Add Column
              </Button>
            </>
          )}
        />
      </fieldset>
    </>
  );
};

export default LagTableForm;
